import { NavLink } from 'react-router-dom';

/**
 * Sprint AA — 下部固定タブナビゲーション (旧 `old/apps/user/src/pages/MainPage.tsx` L173-178 踏襲)。
 *
 * 旧の表面 API (`grid grid-cols-3` + `border-t border-neutral-200 bg-white` +
 * `paddingBottom: env(safe-area-inset-bottom, 0px)`) を v2 規約 (Tailwind preset
 * semantic token + react-router NavLink active 判定) で再実装。
 *
 * URL pattern (基本路 / Provider 配下 / `:sessionId` 配下のいずれにも対応):
 *   - urlToken 未指定 (fallback `/` 経路): `/map`, `/post`, `/list`
 *   - urlToken 指定 (`/m/:urlToken/`): `/m/{urlToken}/map`, `/m/{urlToken}/post`, `/m/{urlToken}/list`
 *
 * 旧と v2 の差分 (細部 UX):
 *   - 旧 neutral-200 → v2 slate-200 (semantic token 統一、Tailwind preset)
 *   - active 表示: NavLink の isActive で primary-50 / primary-700 (旧の brand- 系と同コンセプト)
 *   - z-30 (Drawer の z-40 / z-50 と modal の z-50+ より下)
 *
 * 親 page 側で `pb-16` 等で BottomTabBar 分の余白を確保する責務あり。
 */

export interface BottomTabBarProps {
  /** `/m/:urlToken/` 配下なら指定、fallback (`/`) 時は undefined。 */
  urlToken?: string;
}

// Sprint BG-UserMenu: 下部タブのアイコンテイストを「ぬり絵文字」系で揃える
// (旧 🗺/✏/📋 → 📍/📝/📋。旧の 🗺/✏ は線画系で 📋 と質感が混在していた)
const TABS = [
  { to: 'map', label: 'マップ', icon: '📍' },
  { to: 'post', label: '投稿', icon: '📝' },
  { to: 'list', label: '一覧', icon: '📋' },
] as const;

export function BottomTabBar({ urlToken }: BottomTabBarProps) {
  const basePath = urlToken ? `/m/${urlToken}` : '';
  return (
    <nav
      className="fixed bottom-0 inset-x-0 grid grid-cols-3 border-t border-slate-200 bg-white z-30"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      aria-label="メインナビゲーション"
    >
      {TABS.map((tab) => (
        <NavLink
          key={tab.to}
          to={`${basePath}/${tab.to}`}
          end
          className={({ isActive }) =>
            `flex flex-col items-center justify-center min-h-touch py-2 text-xs ${
              isActive ? 'text-primary-700 bg-primary-50' : 'text-slate-600 hover:bg-slate-50'
            }`
          }
        >
          <span className="text-lg leading-none mb-0.5" aria-hidden="true">
            {tab.icon}
          </span>
          <span className="leading-none">{tab.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}
